import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import InboxTabs from '../../components/admin/InboxTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['', 'sent', 'delivered', 'delayed', 'bounced', 'failed', 'complained'];
const TONE = { delivered: 'bg-[#e9eddf] text-[#536543]', sent: 'bg-zinc-100 text-zinc-600', delayed: 'bg-amber-50 text-amber-700', bounced: 'bg-red-50 text-red-700', failed: 'bg-red-50 text-red-700', complained: 'bg-red-50 text-red-700', sending: 'bg-zinc-100 text-zinc-500' };
const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export default function AdminEmailActivityPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (status) p.set('status', status); if (q.trim()) p.set('q', q.trim());
    axios.get(`${API_URL}/api/admin/emails/activity?${p}`, { headers }).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {}, error: true }));
  }, [headers, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const failed = (data?.counts?.failed || 0) + (data?.counts?.bounced || 0);

  return (
    <div className="gg-ws min-h-screen bg-[#f4f2ed]" data-testid="email-activity-page">
      <InboxTabs />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-9 py-6">
        <h1 className="text-lg font-semibold tracking-tight text-[#20211d]">Email Activity</h1>
        <p className="text-sm text-[#77776e] mt-1">Every CRM email with its live Resend delivery status. "Sent" means accepted for sending.</p>
        {failed > 0 && <Link to="/admin/emails/failed" className="inline-block mt-3 text-xs font-semibold text-[#b64e13]" data-testid="email-activity-failed-link">{failed} failed or bounced — review & retry ↗</Link>}
        <div className="flex flex-wrap gap-2 mt-5 items-center">
          {STATUSES.map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-semibold border capitalize ${status === s ? 'bg-[#252a20] text-white border-[#252a20]' : 'bg-[#fbfaf7] border-[#dcded2] text-[#5f6455]'}`} data-testid={`email-filter-${s || 'all'}`}>
              {s || 'All'}{s && data?.counts?.[s] ? ` · ${data.counts[s]}` : ''}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72"><Search className="h-4 w-4 absolute left-3 top-2.5 text-[#8c8e82]" /><Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" data-testid="email-search" /></div>
        </div>
        <div className="mt-4 rounded-xl border border-[#e7e4db] bg-[#fffefa] divide-y divide-[#e7e4db]" data-testid="email-activity-list">
          {data === null ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-[#F56F05]" /></div> : !data.items.length ? <p className="p-8 text-sm text-[#77776e] text-center" data-testid="email-activity-empty">{data.error ? 'Could not load email activity.' : 'No emails match.'}</p> : data.items.map((e) => (
            <div key={e.id} className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2" data-testid={`email-row-${e.id}`}>
              <div className="min-w-0"><p className="text-sm font-semibold text-[#20211d] truncate">{e.subject}</p><p className="text-[11px] text-[#77776e] truncate">To {e.to} · {(e.purpose || '').replace(/_/g, ' ')} · {fmt(e.created_at)}</p>{e.error && <p className="text-[11px] text-red-600 mt-0.5">{e.error}</p>}</div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded h-fit w-fit ${TONE[e.status] || TONE.sent}`}>{e.status}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
